/**
 * Eén rijprofiel voor alles wat in de hal beweegt: rijden, heffen, dalen,
 * vorken uitschuiven, draaien (feedback 21 oktober, "realistischer").
 *
 * De trucks bewogen eerst met een vaste vorm per beweging: 15 % optrekken en
 * 15 % afremmen ongeacht de afstand, of een smoothstep, of een ease-in-out
 * waarin een truck nooit op snelheid kwam. Dat gaf ruim 2 m/s² bij het
 * optrekken en hefsnelheden tot veertien keer die van het typeblad — het
 * soort beweging dat je als animatie herkent.
 *
 * Hier wordt elke beweging begrensd door een topsnelheid en een maximale
 * versnelling. De versnelling loopt op en af als een halve sinus, dus elke
 * beweging begint en eindigt met snelheid én versnelling nul: geen schok bij
 * het wegrijden en geen tik bij het stoppen. Een korte beweging haalt de
 * topsnelheid niet en is dan één vloeiende boog.
 *
 * Puur, zonder imports: `scripts/controleer-bewegingen.mjs` toetst het buiten
 * de browser.
 */

export interface Profiel {
  /** Afstand, altijd ≥ 0 (in meters of radialen). */
  weg: number
  /** Snelheid op het vlakke stuk. */
  top: number
  /** Duur van het optrekken, en van het afremmen. */
  tOp: number
  /** Duur van het vlakke stuk op topsnelheid. */
  tVast: number
  duur: number
}

/**
 * Het profiel voor een afstand `weg` met topsnelheid `vmax` en maximale
 * versnelling `amax`. `minDuur` rekt een heel korte beweging op (een vork die
 * zes centimeter zakt duurt toch even), zonder de vorm te veranderen.
 */
export function maakProfiel(weg: number, vmax: number, amax: number, minDuur = 0): Profiel {
  const d = Math.abs(weg)
  if (d < 1e-9) return { weg: 0, top: 0, tOp: 0, tVast: 0, duur: minDuur }
  let top = vmax
  let tOp = (Math.PI * top) / (2 * amax)
  // Optrekken en afremmen leggen samen top · tOp af.
  let tVast = (d - top * tOp) / top
  if (tVast < 0) {
    top = Math.sqrt((2 * amax * d) / Math.PI)
    tOp = (Math.PI * top) / (2 * amax)
    tVast = 0
  }
  let duur = 2 * tOp + tVast
  if (duur < minDuur) {
    const k = minDuur / duur
    tOp *= k
    tVast *= k
    top /= k
    duur = minDuur
  }
  return { weg: d, top, tOp, tVast, duur }
}

/** Afgelegde weg, snelheid en versnelling op tijd `t` binnen het profiel. */
export function opProfiel(p: Profiel, t: number): { s: number; v: number; a: number } {
  if (p.weg === 0 || t <= 0) return { s: 0, v: 0, a: 0 }
  // Precies op het doel: daar gaat een pallet over, dus geen afrondingsfout.
  if (t >= p.duur) return { s: p.weg, v: 0, a: 0 }
  const V = p.top
  const T = p.tOp
  const w = Math.PI / T
  const op = (u: number) => ({
    s: (V / 2) * (u - Math.sin(w * u) / w),
    v: (V / 2) * (1 - Math.cos(w * u)),
    a: (V / 2) * w * Math.sin(w * u),
  })
  if (t < T) return op(t)
  if (t < T + p.tVast) return { s: (V * T) / 2 + V * (t - T), v: V, a: 0 }
  const r = op(p.duur - t)
  return { s: p.weg - r.s, v: r.v, a: -r.a }
}

/** Het deel van de weg (0..1) dat er op tijd `t` op zit. */
export const deelVan = (p: Profiel, t: number): number => (p.weg === 0 ? (t >= p.duur ? 1 : 0) : opProfiel(p, t).s / p.weg)

/**
 * Eén stap in een reeks: de kanalen in `doel` bewegen samen volgens
 * `profiel` van waar ze waren naar hun doel. `gebeurt` valt aan het eind van
 * de stap, als alles stilstaat.
 */
export interface Stap<K extends string> {
  doel: Partial<Record<K, number>>
  profiel: Profiel
  gebeurt?: string
}

export const duurVan = <K extends string>(stappen: Stap<K>[]): number =>
  stappen.reduce((t, s) => t + s.profiel.duur, 0)

/**
 * De stand op tijd `t` in een reeks stappen, vanuit `begin`. `laatste` is de
 * laatste gebeurtenis waarvan de stap af is.
 */
export function volg<K extends string>(stappen: Stap<K>[], begin: Record<K, number>, t: number):
  { kanalen: Record<K, number>; laatste?: string } {
  const kanalen = { ...begin }
  let laatste: string | undefined
  let tijd = 0
  for (const s of stappen) {
    const lokaal = t - tijd
    if (lokaal <= 0) break
    const e = deelVan(s.profiel, lokaal)
    for (const sleutel of Object.keys(s.doel) as K[]) {
      kanalen[sleutel] += (s.doel[sleutel]! - kanalen[sleutel]) * e
    }
    if (lokaal >= s.profiel.duur && s.gebeurt) laatste = s.gebeurt
    tijd += s.profiel.duur
  }
  return { kanalen, laatste }
}
